import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { MessageCircle, CheckCircle2, Share2, AlertTriangle } from "lucide-react";

/**
 * Free "one-click" WhatsApp reminders.
 * Each button is a wa.me link: it opens WhatsApp (app or WhatsApp Web) on the admin's own
 * phone/computer with the reminder already typed — the admin just presses Send.
 */
export default function WhatsAppReminderDialog({ open, onOpenChange, day }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get("/alerts/reminder-links", { params: day ? { day } : {} }).then((r) => setData(r.data));
  }, [open, day]);

  const markSent = (mid) => {
    setData((d) => ({ ...d, items: d.items.map((i) => (i.manager_id === mid ? { ...i, sent_today: true } : i)) }));
    api.post(`/alerts/reminder-opened/${mid}`, null, { params: { day: data?.date } }).catch(() => {});
  };

  const items = data?.items || [];
  const sendable = items.filter((i) => i.link && i.opted_in);
  const done = sendable.filter((i) => i.sent_today).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121215] border-[#27272a] text-zinc-100 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-400" /> Send WhatsApp reminders
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Click <b>Open WhatsApp</b> next to each person. WhatsApp opens with the message ready — just press
            <b> Send</b>, then come back here for the next one.
          </DialogDescription>
        </DialogHeader>

        {!data && <div className="py-8 text-center text-zinc-500">Loading…</div>}

        {data && !data.is_working_day && (
          <div className="py-8 text-center text-zinc-400">This is not a working day — no reminders needed.</div>
        )}

        {data && data.is_working_day && items.length === 0 && (
          <div className="py-8 text-center text-emerald-300">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2" /> Everyone has submitted their EOD. Nothing to send!
          </div>
        )}

        {data && items.length > 0 && (
          <>
            <div className="text-xs text-zinc-500 -mt-1">
              {done}/{sendable.length} sent · {items.length} not submitted for {data.date}
            </div>
            <div className="max-h-[50vh] overflow-y-auto divide-y divide-[#1f1f23] rounded-lg border border-[#27272a]">
              {items.map((i) => (
                <div key={i.manager_id} className="p-3 flex items-center gap-3" data-testid={`wa-row-${i.manager_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{i.name}</span>
                      {i.sent_today && (
                        <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px]">Sent</Badge>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">
                      {i.number || "No WhatsApp number"} {i.number && "· "}{i.number && i.message}
                    </div>
                  </div>
                  {!i.number ? (
                    <span className="text-xs text-amber-300 flex items-center gap-1 shrink-0">
                      <AlertTriangle className="w-3 h-3" /> Add number in Managers
                    </span>
                  ) : !i.opted_in ? (
                    <span className="text-xs text-zinc-500 shrink-0">Reminders off</span>
                  ) : (
                    <Button asChild size="sm" variant={i.sent_today ? "ghost" : "outline"}
                      className={i.sent_today ? "text-zinc-400 shrink-0"
                        : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 shrink-0"}>
                      <a href={i.link} target="_blank" rel="noopener noreferrer"
                        data-testid={`wa-open-${i.manager_id}`}
                        onClick={() => markSent(i.manager_id)}>
                        <MessageCircle className="w-4 h-4 mr-1" /> {i.sent_today ? "Send again" : "Open WhatsApp"}
                      </a>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {data && data.is_working_day && (
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#27272a]">
            <div className="text-xs text-zinc-500 min-w-0 truncate">Summary: {data.summary.message}</div>
            <Button asChild size="sm" variant="outline" className="border-[#27272a] shrink-0">
              <a href={data.summary.link} target="_blank" rel="noopener noreferrer" data-testid="wa-share-summary">
                <Share2 className="w-4 h-4 mr-1" /> Share summary to a group
              </a>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
